import { useCallback, useMemo } from 'react';
import { MotionType, SafetyPredictionTrigger, Unit } from '@/api/ui/requests';
import '@/extensions/arrayExtensions';
import { DEFAULT_SAFETY_PREDICTION_EXTENDED_PERIOD, DEFAULT_SAFETY_PREDICTION_PERIOD } from '@/features/SafetyPrediction/Overview/types';
import { motionAsTrigger } from '@/features/SafetyPrediction/safetyPredictionUtils';
import { LicenseFeatures, SafetyPredictionTriggerStaticValues } from '@/types';
import useLicence from './useLicence';
import useOrganisation from './useOrganisation';

const RAK_PORTS_ORG_NAME = 'RAK Ports';

export type GraphConfigurationProps = {
  defaultLabel: string;
  defaultUnit: Unit;
  convertedUnit?: Unit;
};

export function useGraphConfig() {
  const { hasFeature } = useLicence();
  const { selectedOrganisation } = useOrganisation();

  const graphConfiguration = useMemo(() => selectedOrganisation?.graphConfiguration, [selectedOrganisation?.graphConfiguration]);
  const extendedPredictionPeriod = useMemo(
    () =>
      hasFeature(LicenseFeatures.EXTENDED_PREDICTION_PERIOD) ? DEFAULT_SAFETY_PREDICTION_EXTENDED_PERIOD : DEFAULT_SAFETY_PREDICTION_PERIOD,
    [hasFeature]
  );

  const getDefaultGraphConfig = useCallback(
    (trigger: SafetyPredictionTrigger): GraphConfigurationProps => {
      const { defaultLabel, defaultUnit } = SafetyPredictionTriggerStaticValues[trigger];
      const maxThresholdConfig = graphConfiguration?.thresholdsByTriggers
        ?.filter((threshold) => threshold.trigger === trigger)
        ?.flatMap((threshold) => threshold.thresholds)
        ?.maxBy((threshold) => threshold.percentage);

      return {
        defaultLabel: maxThresholdConfig?.abbr ?? defaultLabel,
        defaultUnit: defaultUnit,
        convertedUnit: maxThresholdConfig?.unit ?? defaultUnit,
      };
    },
    [graphConfiguration?.thresholdsByTriggers]
  );

  const motionGraphConfiguration = useCallback(
    (motionType: MotionType): GraphConfigurationProps => {
      return getDefaultGraphConfig(motionAsTrigger(motionType));
    },
    [getDefaultGraphConfig]
  );

  const lineForceConfiguration = useMemo(() => getDefaultGraphConfig(SafetyPredictionTrigger.LINE_FORCE), [getDefaultGraphConfig]);
  const fenderDeflectionConfiguration = useMemo(
    () => getDefaultGraphConfig(SafetyPredictionTrigger.FENDER_DEFLECTION),
    [getDefaultGraphConfig]
  );
  const bollardForceConfiguration = useMemo(() => getDefaultGraphConfig(SafetyPredictionTrigger.BOLLARD_FORCE), [getDefaultGraphConfig]);

  return {
    defaultPeriod: selectedOrganisation?.name === RAK_PORTS_ORG_NAME ? 7 : graphConfiguration?.defaultPeriod ?? extendedPredictionPeriod,
    lineForceConfiguration,
    fenderDeflectionConfiguration,
    bollardForceConfiguration,
    motionGraphConfiguration,
    getDefaultGraphConfig,
  };
}
